import axios from 'axios'
import moment from 'moment-timezone'
import {
    ensureFreshAccessToken,
    ensureFreshSanctumSession,
    getAccessToken,
    isSanctumSession,
} from '@src/libs/authClient'

if (import.meta.env.DEV) {
    axios.defaults.baseURL = window.location.origin //+ '/api'
} else {
    axios.defaults.baseURL = window.env.APP_URL //+ '/api'
}
axios.defaults.headers.common['timezone'] = moment.tz.guess()
axios.defaults.withCredentials = true
axios.defaults.withXSRFToken = true
// O backend só usa o prefixo __Host- (StatelessCsrfToken) quando a conexão é
// HTTPS de verdade — em http puro (dev local) o navegador nem aceitaria um
// cookie __Host-, então o nome tem que bater com o mesmo critério do server.
axios.defaults.xsrfCookieName =
    window.location.protocol === 'https:'
        ? '__Host-XSRF-TOKEN-API'
        : 'XSRF-TOKEN-API'

// Só anexa o token quando o destino é este próprio backend: uma URL absoluta
// pra outro host (CEP, Meta, Google, upload direto pra um bucket) não deve
// levar o access token/ponte Sanctum junto. URL relativa é sempre same-origin
// (cai no `axios.defaults.baseURL`).
function isRequestToOwnBackend(url?: string): boolean {
    if (!url) {
        return true
    }
    if (!/^https?:\/\//i.test(url)) {
        return true
    }

    try {
        return new URL(url).origin === new URL(axios.defaults.baseURL!).origin
    } catch {
        return false
    }
}

// Anexa o JWT do pcare-auth em toda request pra este backend, renovando-o
// ANTES de sair se já estiver (ou estiver perto de estar) expirado — em vez
// de esperar o 401 pra só então reagir (ver o interceptor de resposta em
// plugins/router.ts, que continua existindo como rede de segurança).
axios.interceptors.request.use(async (config) => {
    if (!isRequestToOwnBackend(config.url)) {
        return config
    }

    // Sessão Sanctum (autologin por link, convite, impersonação): a sessão
    // inteira mora no cookie httpOnly `token`, que o browser já anexa sozinho.
    // Nada de Authorization aqui — o header teria precedência sobre o cookie no
    // backend, e o JWT em memória é justamente quem NÃO deve valer agora.
    if (isSanctumSession()) {
        await ensureFreshSanctumSession().catch(() => {
            // Mesma lógica do JWT abaixo: deixa a request sair e o 401 decidir.
        })
        return config
    }

    await ensureFreshAccessToken().catch(() => {
        // Falhar em renovar não impede a request de sair: ela vai falhar com
        // 401 do jeito normal, e o interceptor de resposta decide o que
        // fazer (deslogar, mandar pro login de novo).
    })

    const token = getAccessToken()
    if (token) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

export default axios
